import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "../../../../helper/HwIcon";
import { getCookie } from "../../../../helper/commonFunction";

const Ico = {
    check: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
            <path
                d="M5 12.5l4.5 4.5L19 7"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    ),
    clock: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.9" />
            <path
                d="M12 7.5V12l3 2"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    ),
    cross: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
            <path
                d="M7 7l10 10M17 7L7 17"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
            />
        </svg>
    ),
};

const statusData = {
    CONFIRMED: {
        text: "Booking Confirmed",
        msg: "Thank you! Your flight booking is confirmed and your tickets are on their way to your email.",
    },
    HOLD: {
        text: "Booking On Hold",
        msg: "Thank you! Your ticket is on hold. Please note down your reference id and complete payment before expiry.",
    },
    PENDING: {
        text: "Booking Pending",
           msg: "Your booking is currently pending confirmation. Please check your booking details later for update.",
    },
    UNCONFIRMED: {
        text: "Booking Unconfirmed",
        msg: "Your booking is unconfirmed or the ticket hold has expired.",
    },
    "HOLD-RELEASE": {
        text: "Booking Unconfirmed",
        msg: "Your ticket hold has been released and the booking is unconfirmed.",
    },
    HOLD_RELEASE: {
        text: "Booking Unconfirmed",
        msg: "Your ticket hold has been released and the booking is unconfirmed.",
    },
    CANCELLED: {
        text: "Booking Cancelled",
        msg: "Your flight booking has been cancelled.",
    },
    CANCEL: {
        text: "Booking Cancelled",
        msg: "Your flight booking has been cancelled.",
    },
    FAILED: {
        text: "Booking Failed",
        msg: "We were unable to complete your flight booking. Please try again or contact support.",
    },
    ABORTED: {
        text: "Booking Aborted",
        msg: "The booking process was aborted before completion. Please try again.",
    },
};

const CANCEL_STATUSES = ["cancel", "cancelled", "aborted", "failed", "unconfirmed", "hold-release", "hold_release"];
const FAILED_STATUSES = ["aborted", "failed", "unconfirmed"];

const BookingConfirmationBanner = ({
    currentTab,
    queryParams,
    bookingStatus,
    bookingOtherData,
}) => {
    const navigate = useNavigate();

    if (!currentTab?.["3"]?.active && !queryParams?.bookingId && !queryParams?.refreshPage) return null;

    const rawStatus = (bookingStatus || bookingOtherData?.bookingStatus || "").toUpperCase().trim();
    const queryStatus = (queryParams?.status || "").toUpperCase().trim();
    const orderStatus = (bookingOtherData?.orders?.status || bookingOtherData?.status || "").toUpperCase().trim();
    const bookingId = queryParams?.bookingId;

    const isPendingValue = (s) => {
        if (!s) return false;
        return (
            s.includes("PENDING") ||
            s === "IN_PROCESS" ||
            s === "IN_PROGRESS" ||
            s === "PROCESSING" ||
            s === "ON_REQUEST" ||
            s === "STATUS_REQUESTED"
        );
    };

    const isHoldReleaseValue = (s) => {
        if (!s) return false;
        return s === "HOLD-RELEASE" || s === "HOLD_RELEASE" || s === "HOLD RELEASE";
    };

    const isHoldValue = (s) => {
        if (!s) return false;
        return s === "HOLD";
    };

    const isCancelValue = (s) => {
        if (!s) return false;
        return (
            s.includes("CANCEL") ||
            s === "ABORTED" ||
            s === "FAILED" ||
            s === "UNCONFIRMED"
        );
    };

    // Resolve unified normalized key
    let resolvedStatusKey = "CONFIRMED";
    if (isHoldReleaseValue(rawStatus) || isHoldReleaseValue(queryStatus) || isHoldReleaseValue(orderStatus)) {
        resolvedStatusKey = "HOLD_RELEASE";
    } else if (isHoldValue(rawStatus) || isHoldValue(queryStatus) || isHoldValue(orderStatus)) {
        resolvedStatusKey = "HOLD";
    } else if (isPendingValue(rawStatus) || isPendingValue(queryStatus) || isPendingValue(orderStatus)) {
        resolvedStatusKey = "PENDING";
    } else if (isCancelValue(rawStatus) || isCancelValue(queryStatus) || isCancelValue(orderStatus)) {
        resolvedStatusKey = "CANCELLED";
    } else if (rawStatus === "CONFIRMED" || queryStatus === "CONFIRMED" || orderStatus === "CONFIRMED") {
        resolvedStatusKey = "CONFIRMED";
    }

    const isHold = resolvedStatusKey === "HOLD";
    const isPending = resolvedStatusKey === "PENDING";
    const isCancel = CANCEL_STATUSES.includes(resolvedStatusKey.toLowerCase()) || FAILED_STATUSES.includes(resolvedStatusKey.toLowerCase());

    const statusIcon = isCancel ? Ico.cross : isPending ? Ico.clock : Ico.check;
    const tone = isCancel ? "is-cancel-hotel" : isPending || isHold ? "is-pending" : "is-confirm-hotel";

    const formattedBookingId =
        queryParams?.source === "tripjack"
            ? bookingId?.replace(/^(TJS|TJ)/, "")
            : queryParams?.source === "ndc-portal"
                ? bookingId?.replace(/^(NDCEG-BR-)/, "")
                : bookingId;

    const currentStatusConfig = statusData[resolvedStatusKey] || statusData.CONFIRMED;
    const heading = currentStatusConfig.text;
    const message = isHold && formattedBookingId
        ? `Thank you! Your Ticket is on hold. Please note down reference id: ${formattedBookingId}.`
        : currentStatusConfig.msg;

    const handleBackToSearch = () => {
        // sessionStorage.removeItem("ndcData");
        // sessionStorage.removeItem("ndcDataBundle");
        // const lastQueryParams = getCookie("lastQueryParamsFlight");
        // if (lastQueryParams) {
        //     navigate(`/flight/list${lastQueryParams}`);
        // } else {
            navigate("/flight/search");
        // }
    };

    return (
        <div className={`hw-exchk--hero hw-hotel--hero hw-flight--hero hw-cfhero ${tone}`}>
            <div className="hw-exchk--heroInner">
                <button
                    type="button"
                    className="hw-exchk--back hw-cfhero--back"
                    onClick={handleBackToSearch}
                >
                    <ChevronLeftIcon /> Back to search
                </button>
                <div className="hw-cfhero--bar">
                    <div className="hw-cfhero--left">
                        <span className="hw-cfhero--badge">{statusIcon}</span>
                        <div>
                            <h1 className="hw-cfhero--title">{heading}</h1>
                            <p className="hw-cfhero--sub">{message}</p>
                        </div>
                    </div>

                    <div className="hw-cfhero--id">
                        {formattedBookingId ? (
                            <span className="hw-cfhero--idVal">
                                Booking ID : {formattedBookingId}
                            </span>
                        ) : null}

                        {queryParams?.source === "ndc-portal" && bookingOtherData?.gdsPnr ? (
                            <span
                                className="hw-cfhero--idVal"
                                style={{ fontSize: "13px", opacity: 0.9, marginTop: 4 }}
                            >
                                GDS PNR : {bookingOtherData.gdsPnr}
                            </span>
                        ) : null}

                        {bookingOtherData?.holdExpiryDate &&
                        (queryStatus === "HOLD" || isHold) ? (
                            <span
                                className="hw-cfhero--idVal fbd-hold-expiry"
                                style={{ fontSize: "13px", marginTop: 6 }}
                            >
                                Hold Expires On: {bookingOtherData.holdExpiryDate}
                            </span>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(BookingConfirmationBanner);
